import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useHistory, Redirect } from 'react-router-dom';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { setStep, setSchoolName } from '../habitationSlice';
import HabitationLayout from '../components/layouts/HabitationLayout';
import AddressStep from '../steps/AddressStep';
import HousingTypeStep from '../steps/HousingTypeStep';
import HousingDetailsRoomStep from '../steps/HousingDetailsRoomStep';
import HousingDetailsFullStep from '../steps/HousingDetailsFullStep';
import PersonalInformationStep from '../steps/PersonalInformationStep';
import { HousingType } from '../types';
import { isAuthenticated } from '@/services/utils';
import { getMe } from '@/api/authApi';
import LocalStorageService from '@/services/LocalStorageService';
import RecapAndSignatureStep from '../steps/RecapAndSignatureStep';

const Spacing = styled.div`
  height: 50px;
  @media (max-width: 600px) {
    height: 10px;
  }
`;

const HabitationV2Page = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();

  const step = useAppSelector(state => state.habitation.step);
  const selectedHousing = useAppSelector(state => state.habitation.housingType);
  const lastVerifiedStep = useAppSelector(state => state.habitation.lastVerifiedStep);
  const [isLogged, setIsLogged] = useState<boolean>(!!isAuthenticated());
  const [authChecked, setAuthChecked] = useState(false);
  const isTempRegistered = useAppSelector(state => state.user.isTempRegistered);

  useEffect(() => {
    let cancelled = false;
    const verifyAuth = async () => {
      const localUser = isAuthenticated();
      if (!localUser) {
        if (!cancelled) {
          setIsLogged(false);
          setAuthChecked(true);
        }
        return;
      }
      try {
        await getMe();
        if (!cancelled) {
          setIsLogged(true);
          setAuthChecked(true);
        }
      } catch {
        LocalStorageService.clearUser();
        LocalStorageService.clearToken();
        if (!cancelled) {
          setIsLogged(false);
          setAuthChecked(true);
        }
      }
    };
    verifyAuth();
    return () => { cancelled = true; };
  }, []);

  // 🧠 Gère la logique de redirection et d'initialisation des steps
  useEffect(() => {

    const params = new URLSearchParams(location.search);

    // Capture ?school=... à l'arrivée pour rattacher l'inscription temporaire
    // à la bonne école si l'utilisateur n'est pas encore connecté.
    const schoolParam = params.get('school');
    if (schoolParam) {
      dispatch(setSchoolName(schoolParam.trim().toLowerCase()));
    }

    const stepParam = parseInt(params.get('step') || '1', 10);
    const desiredStep = isNaN(stepParam) ? 1 : stepParam;

    // Garde sécurité
    if (desiredStep > lastVerifiedStep + 1 ) {
      params.set('step', `${lastVerifiedStep + 1}`);
      history.replace({ search: params.toString() });
      return;
    }

    if (desiredStep < 1) {
      params.set('step', `${lastVerifiedStep + 1}`);
      history.replace({ search: params.toString() });
      return;
    }

    if (step !== desiredStep && desiredStep != 0) {
      dispatch(setStep(desiredStep));
    }
  }, [selectedHousing, dispatch]);


  // 🔁 Sync Redux vers l'URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlStep = parseInt(params.get('step') || '1', 10);
    const actualStep = step ;

    if (urlStep !== actualStep) {
      params.set('step', actualStep.toString());
      history.replace({ search: params.toString() });
    }
  }, [step, location.search, history]);

  // 📦 Rendu de l'étape
  const renderStep = () => {
    switch (step) {
      case 1:
        return <AddressStep />;
      case 2:
        return <HousingTypeStep />;
      case 3:
        return selectedHousing === HousingType.room
          ? <HousingDetailsRoomStep />
          : <HousingDetailsFullStep />;
      case 4:
        if (!authChecked) return null;
        return isLogged || isTempRegistered ? <RecapAndSignatureStep /> : <PersonalInformationStep />;
      default:
        return <Redirect to="/insurance/housing?step=1" />;
    }
  };

  return (
    <HabitationLayout>
      <Spacing />
      {renderStep()}
    </HabitationLayout>
  );
};

export default HabitationV2Page;
